import React, { useState, useEffect } from 'react';
import { Card, Button, Space, Collapse, Alert, Spin, Empty, Typography, Table, Tag, Divider } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { baseUrl_SPB } from '../../BaseUrl';
import axios from 'axios';
import WorkHoursPanel from './panels/WorkHoursPanel';
import UnavailabilityPanel from './panels/UnavailabilityPanel';
import LeavePanel from './panels/LeavePanel';
import TimesheetPanel from './panels/TimesheetPanel';
import WorkPreferencesPanel from './panels/WorkPreferencesPanel';
import EmploymentConditionsPanel from './panels/EmploymentConditionsPanel';
import ProfessionalDetailsPanel from './panels/ProfessionalDetailsPanel';
import EmergencyContactPanel from './panels/EmergencyContactPanel';
import OnboardingDocumentsPanel from './panels/OnboardingDocumentsPanel';
import LicenceMembershipPanel from './panels/LicenceMembershipPanel';
import { fetchOnboardingStatus } from '../../services/OnboardingService';
import { isExpiredDate } from './panels/expiryUtils';
import { useAuth } from '../../AuthProvider';
import AccessManagementPanel from '../../access-control/AccessManagementPanel';

const { Text } = Typography;
import usePermissions from '../../access-control/auth/usePermissions';

const buildLicenceLabel = (record) => {
  return [record.type, record.memOrLicenceNumber].filter(Boolean).join(' - ') || 'Untitled record';
};

const EmployeeProfilePanels = ({ employee, token, onDataUpdate, isUserManager = false }) => {
  const authContext = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeKeys, setActiveKeys] = useState([]);
  const [hasOnboarding, setHasOnboarding] = useState(false);
  const [expiredLicences, setExpiredLicences] = useState([]);

  const loggedInPermissionIds = authContext?.additionalUserInfo?.permissionIds;
  const loggedInPermissionSetIds = authContext?.additionalUserInfo?.permissionSetIds;
  const resolvedLoggedInPermissions = [
    ...(Array.isArray(loggedInPermissionIds) ? loggedInPermissionIds : []),
    ...(Array.isArray(loggedInPermissionSetIds)
      ? loggedInPermissionSetIds : []),
  ];
  const canManageEmployeeAccessOld = resolvedLoggedInPermissions.length
    ? resolvedLoggedInPermissions.includes('USER_ACCESS_EDIT')
    : false;


  const {
    canManageEmployeeAccess,

  } = usePermissions();
  useEffect(() => {
    if (!employee?.id || !token) return;
    fetchOnboardingStatus(token, employee.id)
      .then((result) => setHasOnboarding(!!result))
      .catch(() => setHasOnboarding(false));
  }, [employee?.id, token]);

  const refreshLicenceWarnings = async (prefetchedRecords) => {
    if (!employee?.id || !token) {
      setExpiredLicences([]);
      return;
    }

    try {
      const records = Array.isArray(prefetchedRecords)
        ? prefetchedRecords
        : await fetch(`${baseUrl_SPB}/api/v1/onboarding/licence/staff/${employee.id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }).then((response) => (response.ok ? response.json() : []));

      const nextExpired = (Array.isArray(records) ? records : []).filter((record) => isExpiredDate(record?.expiryDate));
      setExpiredLicences(nextExpired);
    } catch (warningError) {
      console.error(warningError);
      setExpiredLicences([]);
    }
  };

  useEffect(() => {
    refreshLicenceWarnings();
  }, [employee?.id, token]);

  if (!employee?.id) {
    return <Empty description="No employee selected" />;
  }

  const panels = [
    {
      key: 'work-hours',
      label: 'Work Hours',
      description: 'Work statistics and hours summary',
      children: (
        <WorkHoursPanel
          employeeId={employee.id}
          employeeName={`${employee.firstName || ''} ${employee.lastName || ''}`}
          token={token}
        />
      ),
    },
    {
      key: 'unavailability',
      label: 'Unavailability',
      description: 'Manage when employee cannot work',
      children: (
        <UnavailabilityPanel
          employeeId={employee.id}
          token={token}
          onUpdate={() => onDataUpdate?.()}
        />
      ),
    },
    {
      key: 'leave',
      label: 'Leave',
      description: 'View and manage leave requests',
      children: (
        <LeavePanel
          employeeId={employee.id}
          token={token}
          isUserManager={isUserManager}
        />
      ),
    },
    {
      key: 'timesheets',
      label: 'Timesheets',
      description: 'View and manage timesheet entries',
      children: (
        <TimesheetPanel
          employeeId={employee.id}
          token={token}
          isUserManager={isUserManager}
        />
      ),
    },
    {
      key: 'work-preferences',
      label: 'Work Hour Preferences',
      description: 'Set preferred work hours and availability',
      children: (
        <WorkPreferencesPanel
          employeeId={employee.id}
          token={token}
          onUpdate={() => onDataUpdate?.()}
        />
      ),
    },
    {
      key: 'employment-conditions',
      label: 'Employment Conditions',
      description: 'Employment terms and conditions',
      children: (
        <EmploymentConditionsPanel
          employee={employee}
          token={token}
          onUpdate={() => onDataUpdate?.()}
        />
      ),
    },
    {
      key: 'professional-details',
      label: 'Employment & Professional Details',
      description: 'Professional information, experience, and certifications',
      children: (
        <ProfessionalDetailsPanel
          employee={employee}
          token={token}
          onUpdate={() => onDataUpdate?.()}
        />
      ),
    },
    {
      key: 'licences-memberships',
      label: 'Licences / Memberships',
      description: 'Track employee licences, memberships, and expiry dates',
      children: (
        <LicenceMembershipPanel
          employeeId={employee.id}
          token={token}
          onDataChange={refreshLicenceWarnings}
        />
      ),
    },
    ...(isUserManager ? [{
      key: 'emergency-contact',
      label: 'Emergency Contact',
      description: 'Emergency contact details for the employee',
      children: (
        <EmergencyContactPanel
          employee={employee}
          token={token}
          onUpdate={() => onDataUpdate?.()}
        />
      ),
    }] : []),
    ...(hasOnboarding ? [{
      key: 'onboarding-documents',
      label: 'Onboarding Documents',
      description: 'Documents uploaded during employee onboarding',
      children: (
        <OnboardingDocumentsPanel
          employeeId={employee.id}
          token={token}
        />
      ),
    }] : []),
    ...(canManageEmployeeAccess || (authContext?.additionalUserInfo?.role === 'ROLE_CMANAGER') ? [{
      key: 'access-management',
      label: 'Access Management',
      description: 'Manage permission set assignment and effective permissions',
      children: (
        <AccessManagementPanel
          token={token}
          employee={employee}
          employeeId={employee.id}
        />
      ),
    }] : []),
  ];

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      {error && <Alert type="error" showIcon message={error} closable />}
      {expiredLicences.length ? (
        <Alert
          type="warning"
          showIcon
          closable
          message={`Expired licences or memberships found: ${expiredLicences.map((record) => buildLicenceLabel(record)).join(', ')}`}
        />
      ) : null}
      <Collapse
        items={panels}
        activeKey={activeKeys}
        onChange={setActiveKeys}
        style={{
          border: '1px solid #f0f0f0',
          borderRadius: '6px',
        }}
      />
    </div>
  );
};

export default EmployeeProfilePanels;
